import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { posApi } from '@/services/api';

type Props = {
  itemName: string;
  trackBatches?: number | boolean;
  trackSerials?: number | boolean;
  stockQuantity?: number;
};

/** Expandable list of available batches or IMEIs under product stock. */
export default function StockIdentityPreview({
  itemName,
  trackBatches,
  trackSerials,
  stockQuantity = 0,
}: Props) {
  const [open, setOpen] = useState(false);
  const tracked = !!(trackBatches || trackSerials);
  if (!tracked) return null;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['stockIdentity', itemName, trackBatches ? 'batch' : 'serial'],
    queryFn: async () => {
      if (trackSerials) {
        const res = await posApi.getItemSerials(itemName, undefined, 100);
        return { kind: 'serial' as const, rows: (res.data.message || []) as Array<{ imei: string; mac_address?: string }> };
      }
      const res = await posApi.getItemBatches(itemName);
      return {
        kind: 'batch' as const,
        rows: (res.data.message || []) as Array<{ batch_no: string; expiry_date?: string; qty: number }>,
      };
    },
    enabled: open && !!itemName,
    staleTime: 30_000,
  });

  return (
    <div className="mt-1.5 text-left">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-primary-700 hover:text-primary-800"
      >
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {trackSerials ? 'IMEIs' : 'Batches'}
        {stockQuantity > 0 ? ` (${stockQuantity})` : ''}
      </button>
      {open && (
        <div className="mt-1 max-h-36 overflow-y-auto rounded-lg border border-gray-100 bg-gray-50 px-2 py-1.5 text-left">
          {isLoading && <p className="text-[11px] text-gray-500 py-1">Loading…</p>}
          {isError && <p className="text-[11px] text-red-600 py-1">Failed to load</p>}
          {!isLoading && !isError && data?.kind === 'serial' && (
            data.rows.length === 0 ? (
              <p className="text-[11px] text-gray-500 py-1">No available IMEIs</p>
            ) : (
              <ul className="space-y-1">
                {data.rows.map((r) => (
                  <li key={r.imei} className="font-mono text-[11px] text-gray-800 truncate">
                    {r.imei}
                    {r.mac_address ? <span className="text-gray-400"> · {r.mac_address}</span> : null}
                  </li>
                ))}
              </ul>
            )
          )}
          {!isLoading && !isError && data?.kind === 'batch' && (
            data.rows.length === 0 ? (
              <p className="text-[11px] text-gray-500 py-1">No batch stock</p>
            ) : (
              <ul className="space-y-1">
                {data.rows.map((r) => (
                  <li key={r.batch_no} className="text-[11px] text-gray-800 flex justify-between gap-2">
                    <span className="font-mono truncate">
                      {r.batch_no}
                      {r.expiry_date ? <span className="text-amber-700"> · Exp {r.expiry_date}</span> : null}
                    </span>
                    <span className="font-semibold shrink-0">{r.qty}</span>
                  </li>
                ))}
              </ul>
            )
          )}
        </div>
      )}
    </div>
  );
}
